// Centralized analytics API client for admin dashboard
// Provides scalable, token-authenticated requests with graceful fallbacks.

const ADMIN_TOKEN_KEY = 'afd8TvhsdjwiuuvsgjhsAfgsUhjs';

const withTimeout = (promise, ms = 10000) => {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error('Request timeout')), ms))
  ]);
};

const getToken = () => {
  try { return sessionStorage.getItem(ADMIN_TOKEN_KEY); } catch { return null; }
};

async function apiFetch(endpoint, { signal, params = {} } = {}) {
  const token = getToken();
  if (!token) throw new Error('Admin authentication required');

  const url = new URL(endpoint, window.location.origin);
  Object.entries(params).forEach(([k, v]) => { if (v != null) url.searchParams.append(k, v); });

  const res = await withTimeout(fetch(url.toString(), {
    headers: {
      'x-access-token': token,
      'Content-Type': 'application/json'
    },
    signal
  }));

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status} ${res.statusText}: ${text}`);
  }
  return res.json();
}

export async function getAnalyticsSummary({ startDate, endDate, userType, signal } = {}) {
  return apiFetch('/origin/analytics/summary', { signal, params: { startDate, endDate, userType } });
}
export async function getDailyTrends({ startDate, endDate, signal } = {}) {
  return apiFetch('/origin/analytics/daily-trends', { signal, params: { startDate, endDate } });
}
export async function getConversionMetrics({ startDate, endDate, signal } = {}) {
  return apiFetch('/origin/analytics/conversion', { signal, params: { startDate, endDate } });
}
export async function getEngagementMetrics({ startDate, endDate, userType, signal } = {}) {
  return apiFetch('/origin/analytics/engagement', { signal, params: { startDate, endDate, userType } });
}
export async function getRealtimeAnalytics({ signal } = {}) {
  return apiFetch('/origin/analytics/realtime', { signal });
}

// Anti-abuse trial gating metrics (denied due to device/IP reuse)
export async function getTrialGatingMetrics({ startDate, endDate, signal } = {}) {
  return apiFetch('/origin/anti-abuse/trial-gating-metrics', { signal, params: { startDate, endDate } });
}

// Graceful wrapper combining overview pieces
export async function getOverviewComposite({ startDate, endDate, signal } = {}) {
  const controller = new AbortController();
  const combinedSignal = signal || controller.signal;
  try {
    const [summary, daily] = await Promise.all([
      getAnalyticsSummary({ startDate, endDate, signal: combinedSignal }),
      getDailyTrends({ startDate, endDate, signal: combinedSignal })
    ]);
    return { summary, dailyTrends: daily };
  } catch (e) {
    throw e;
  }
}

export function buildDateRange(days = 30) {
  const end = new Date();
  const start = new Date(end.getTime() - (parseInt(days, 10) * 24 * 60 * 60 * 1000));
  return { startDate: start.toISOString(), endDate: end.toISOString() };
}

export { ADMIN_TOKEN_KEY };
